import { useEffect } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import { format, subMonths } from 'date-fns'
import { GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import map from 'ramda/src/map'

import { Address } from '@ecm/data/Address'
import { Article, convertContentToComponents } from '@ecm/data/Article'
import { runEff } from '@ecm/effect'
import { ArticleDefaultImageUrl } from '@ecm/pages/blog'
import NewsletterSignupCta from '@ecm/ui/component/block/NewsletterSignupCta'
import AddressesLinks from '@ecm/ui/component/blog/AddressesLinks'
import ArticleCard from '@ecm/ui/component/blog/ArticleCard'
import ArticleHowTo from '@ecm/ui/component/blog/ArticleHowTo'
import BlogArticleLinks from '@ecm/ui/component/blog/BlogArticleLinks'
import Byline from '@ecm/ui/component/blog/Byline'
import SidebarCta from '@ecm/ui/component/blog/SidebarCta'
import Breadcrumbs, { BreadCrumb } from '@ecm/ui/element/Breadcrumbs'
import Color from '@ecm/ui/element/Color'
import ContentfulImage, { ImageType } from '@ecm/ui/element/ContentfulImage'
import MainContainer from '@ecm/ui/element/MainContainer'
import useSmallScreen from '@ecm/ui/hook/UseSmallScreen'
import DefaultLayout from '@ecm/ui/layout/Default'
import ArticleJsonLd from '@ecm/ui/seo/ArticleJsonLd'
import FaqJsonLd from '@ecm/ui/seo/FaqJsonLd'
import HowToJsonLd from '@ecm/ui/seo/HowToJsonLd'

const DynamicStickyBanner = dynamic(() => import('@ecm/ui/component/StickyBanner'))

type ArticlePageProps = {
  article: Article
  breadCrumb: BreadCrumb[]
  mostRead?: Article[]
  noRobots?: boolean
  pageUrl?: string
  addresses?: Address[]
  relatedArticles?: Article[]
  recentlyAdded?: Article[]
}

export const ArticlePage = ({
  article,
  breadCrumb,
  mostRead,
  noRobots,
  pageUrl,
  addresses,
  recentlyAdded,
  relatedArticles,
}: ArticlePageProps) => {
  const router = useRouter()
  const isSmallScreen = useSmallScreen()

  useEffect(() => {
    if (!router.isReady) {
      return
    }
    if (article.redirectTo) {
      window.location.href = article.redirectTo
    }
  }, [article.redirectTo, router.isReady])

  return (
    <DefaultLayout
      hideFinalCta
      metaImageUrl={`https:${article.mainImageUrl}`}
      metaPermanentUrl={pageUrl}
      metaTwitterCard="summary"
      metaType="article"
      noRobots={noRobots}
      seoTitle={article.seoTitle}
      seoDescription={article.seoDescription}
      canonicalUrl={`blog/${article.slug}`}
    >
      <Box
        sx={{
          background: `linear-gradient(180deg, ${Color.smoke.normal} 120px, rgba(255, 255, 255, 0) 350px)`,
        }}
      >
        <MainContainer sx={{ pt: { md: '4em', xs: '2em' } }}>
          <Breadcrumbs breadCrumbList={breadCrumb} truncateText />
          <Typography variant="h3" component="h1" color={Color.slate.normal}>
            {article.title}
          </Typography>
          <ArticleJsonLd
            author={{
              name: article.author.fullname,
              url: `/blog/author/${article.author.id}`,
            }}
            headline={article.seoTitle}
            description={article.seoDescription.replace(/"/g, '\\"')}
            datePublished={article.publishedDate}
            dateModified={article.publishedDate}
            image={`https:${article.mainImageUrl}`}
            url={`/blog/${article.slug}`}
          />

          <Byline
            author={article.author}
            publishedDate={format(new Date(article.publishedDate), 'MMMM d, yyyy')}
          />
        </MainContainer>
        <Box>
          <MainContainer sx={{ pb: { md: '4em', xs: '2em' } }}>
            <Box
              sx={{
                display: 'grid',
                gap: { lg: '0 9em', md: '0 4em' },
                gridTemplateAreas: {
                  md: `'article readMore' 'line line' 'relatedArticles relatedArticles'`,
                  xs: `'article' 'line' 'relatedArticles' 'readMore'`,
                },
                gridTemplateColumns: {
                  md: '3fr 1fr',
                  xs: '1fr',
                },
                gridTemplateRows: 'auto',
                pt: '4em',
              }}
            >
              <Box
                sx={{
                  gridArea: 'article',
                  img: {
                    borderRadius: '8px',
                    height: 'auto',
                    maxWidth: '100%',
                  },
                  maxWidth: '850px',
                }}
              >
                <Box paddingBottom="2em">
                  <ContentfulImage
                    imageType={ImageType.BlogResponsive}
                    src={`https:${article.mainImageUrl ?? ArticleDefaultImageUrl}`}
                    width={724}
                    height={483}
                    layout="responsive"
                    objectFit="cover"
                    alt={article.title}
                    priority
                  />
                </Box>
                <Box>
                  <Grid
                    container
                    alignItems="stretch"
                    flexDirection="column"
                    flexWrap="nowrap"
                    justifyContent="flex-start"
                  >
                    {convertContentToComponents(article.content)}
                  </Grid>
                  {article.howTo && <ArticleHowTo howTo={article.howTo} />}
                  <HowToJsonLd
                    howTo={article.howTo}
                    image={`https:${article.mainImageUrl ?? ArticleDefaultImageUrl}`}
                    url={`/blog/${article.slug}`}
                  />
                  <Box pt="3em">
                    {article.faq.map(faq => (
                      <Box key={faq.question} pb="1.5em">
                        <Typography variant="p2" component="h2" color={Color.slate.normal}>
                          {faq.question}
                        </Typography>
                        <Typography variant="p2" color={Color.slate.normal}>
                          {faq.answer}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <FaqJsonLd faq={article.faq} />
                </Box>
              </Box>
              <Box
                sx={{
                  gridArea: 'line',
                  hr: {
                    backgroundColor: Color.smoke.darker,
                    border: 'none',
                    height: '1px',
                    left: '0',
                    position: 'absolute',
                    width: '100%',
                  },
                  mb: '2em',
                }}
              >
                <hr />
              </Box>

              <Box sx={{ gridArea: 'readMore' }}>
                {!isSmallScreen && <NewsletterSignupCta small trackGaEvents />}
                {recentlyAdded && recentlyAdded.length > 0 && (
                  <BlogArticleLinks.BlogSidebar heading="Recently Added" articles={recentlyAdded} />
                )}
                {mostRead && mostRead.length > 0 && (
                  <BlogArticleLinks.BlogSidebar heading="Most Read" articles={mostRead} />
                )}
                {!isSmallScreen && <SidebarCta />}
              </Box>
              {relatedArticles && relatedArticles.length > 0 && (
                <Box sx={{ gridArea: 'relatedArticles' }} pt="5em">
                  <NewsletterSignupCta trackGaEvents />
                  <Box pt="5em" pb={{ md: 0, xs: '6em' }}>
                    <Typography variant="h4" component="h2" color={Color.slate.normal}>
                      Related Posts
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: { md: '3em', xs: '2em' },
                        gridTemplateColumns: {
                          md: 'repeat(3, 1fr)',
                          xs: 'repeat(1, 1fr)',
                        },
                        gridTemplateRows: 'auto',
                        justifyContent: 'space-between',
                      }}
                    >
                      {relatedArticles.map(article => {
                        return <ArticleCard key={article.id} article={article} priority={false} />
                      })}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </MainContainer>
          {addresses && addresses.length > 0 && (
            <AddressesLinks.PopularAddresses addresses={addresses} />
          )}
          <DynamicStickyBanner trackingId="stickySignupBanner" eventName="blogBottomButtonClick" />
        </Box>
      </Box>
    </DefaultLayout>
  )
}

export default ArticlePage

export const getStaticProps: GetStaticProps = async ctx => {
  const slug = ctx.params?.slug as string

  const article = await runEff(eff => {
    return eff.getArticle(slug)
  })

  const appUrl = runEff(eff => {
    return eff.ask().appUrl
  })

  const pageUrl = `${appUrl}/blog/${slug}`

  const breadCrumb: BreadCrumb[] = [
    { description: 'Blog', href: '/blog', position: 2 },
    { description: article.title, href: `/blog/${slug}`, position: 3 },
  ]

  const relatedArticles = await runEff(async eff => {
    if (article.categories == null || article.categories.length === 0) {
      return []
    }

    try {
      const categoryId = article.categories[0].id
      const articlesByCategory: Article[] = await eff.getRelatedArticles('shouldLink', categoryId, {
        limit: 300,
        order: '-fields.publishedDate',
      })

      const limit = 3
      const max = articlesByCategory.length - limit // keep the difference between the total and possible max skip at the limit level
      const filteredArticles = articlesByCategory.filter(a => a.id !== article.id)
      const skip =
        filteredArticles.length > limit ? Math.floor(Math.random() * (max - limit) + limit) : 0

      return filteredArticles.slice(skip, articlesByCategory.length).slice(0, limit)
    } catch (e) {
      return []
    }
  })

  const addresses = await runEff(async eff => {
    try {
      const limit = 12

      const addresses = await eff.getAddresses()
      const randomAddresses = addresses.sort(() => Math.random() - Math.random()).slice(0, limit)

      return randomAddresses
    } catch (e) {
      return []
    }
  })

  const recentlyAdded = await runEff(async eff => {
    try {
      const dateDiff = subMonths(new Date(), 6)
      const formattedDate = format(new Date(dateDiff), 'yyyy-MM-dd')
      const articlesByDate: Article[] = await eff.getArticlesByDate(formattedDate, {
        limit: 10,
        order: '-fields.publishedDate',
      })

      const filteredArticles = articlesByDate.filter(a => a.id !== article.id)

      return filteredArticles
    } catch (e) {
      return []
    }
  })

  const mostRead = await runEff(async eff => {
    try {
      const articlesMetadata = await eff.getArticlesMetadataByFieldValue('shouldLink')
      const selectedMostRead: Article[] = await eff.getRelatedArticles('mostRead', undefined, {
        order: '-fields.publishedDate',
      })

      const mostReadLimit = 10
      const skip =
        articlesMetadata.total > mostReadLimit
          ? Math.floor(Math.random() * (articlesMetadata.total - 2 * mostReadLimit) + mostReadLimit)
          : 0

      const randomMostReadArticles: Article[] = await eff.getRelatedArticles(
        'shouldLink',
        undefined,
        {
          limit: mostReadLimit,
          order: '-fields.publishedDate',
          skip: skip,
        }
      )

      const filteredRandomMostRead = randomMostReadArticles.filter(random => {
        if (selectedMostRead.length !== 0) {
          return selectedMostRead.some(selected => random.id !== selected.id)
        }
        return true
      })

      const mostReadArticles = selectedMostRead
        .concat(filteredRandomMostRead)
        .filter(a => a.id !== article.id)
        .slice(0, 10)

      return mostReadArticles
    } catch (e) {
      return []
    }
  })

  return {
    props: {
      addresses,
      article,
      breadCrumb,
      mostRead,
      pageUrl,
      recentlyAdded,
      relatedArticles,
    },
  }
}

export async function getStaticPaths() {
  const articles: Article[] = await runEff(eff => {
    return eff.getArticles()
  })

  const paths = map(a => ({ params: a }), articles)

  return {
    fallback: false,
    paths,
  }
}
